import React, { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Upload, X, Star } from "lucide-react";

const EMPTY = { title: "", location: "", category: "Konut", year: "", description: "", images: [], featured: false, status: "Tamamlandı" };

export default function AdminProjects() {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing("new"); setForm(EMPTY); };
  const openEdit = (p) => { setEditing(p.id); setForm({ ...EMPTY, ...p }); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(
        files.map((f) => {
          const fd = new FormData();
          fd.append("file", f);
          return api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        })
      );
      const newPaths = results.map((r) => r.data.path);
      setForm((prev) => ({ ...prev, images: [...(prev.images || []), ...newPaths] }));
      toast.success(`${files.length} dosya yüklendi`);
    } catch (err) {
      toast.error("Yükleme başarısız: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => setForm((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Başlık zorunlu"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/projects", form);
        toast.success("Proje eklendi");
      } else {
        await api.put(`/projects/${editing}`, form);
        toast.success("Proje güncellendi");
      }
      close();
      load();
    } catch (err) {
      toast.error("Kayıt hatası: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Proje silinsin mi?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Proje silindi");
      load();
    } catch { toast.error("Silme hatası"); }
  };

  const getImg = (p) => (p?.startsWith("http") ? p : fileUrl(p));

  return (
    <div className="p-8 lg:p-12" data-testid="admin-projects-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="section-label mb-3">Proje Yönetimi</div>
          <h1 className="font-heading text-3xl lg:text-4xl text-white tracking-[0.05em]">Projeler ({projects.length})</h1>
        </div>
        <button onClick={openNew} className="btn-gold text-xs" data-testid="add-project-btn"><Plus className="w-4 h-4" /> Yeni Proje</button>
      </div>

      <div className="grid gap-3">
        {projects.map((p) => (
          <div key={p.id} className="yk-card p-4 flex items-center gap-4" data-testid={`project-row-${p.id}`}>
            <div className="w-20 h-20 bg-[#050812] shrink-0 overflow-hidden">
              {p.images?.[0] ? <img src={getImg(p.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-[#111827]"></div>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <div className="text-white font-semibold truncate">{p.title}</div>
                {p.featured && <Star className="w-3.5 h-3.5 text-[#C9A961]" fill="#C9A961" />}
              </div>
              <div className="text-xs text-white/50 uppercase tracking-widest">{p.category} • {p.location} • {p.year}</div>
            </div>
            <button onClick={() => openEdit(p)} className="p-2 text-white/60 hover:text-[#C9A961]"><Edit3 className="w-4 h-4" /></button>
            <button onClick={() => remove(p.id)} className="p-2 text-white/60 hover:text-red-400" data-testid={`delete-project-${p.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {projects.length === 0 && <div className="text-white/40 text-center py-12 border border-dashed border-[#1F2937]">Henüz proje yok</div>}
      </div>

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-[#0A0E1A] border border-[#1F2937] max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8 border-b border-[#1F2937] flex justify-between items-center">
              <h2 className="font-heading text-2xl text-white tracking-widest">{editing === "new" ? "Yeni Proje" : "Projeyi Düzenle"}</h2>
              <button onClick={close} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-8 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Başlık *</label>
                  <input type="text" className="yk-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="project-title-input" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Kategori</label>
                  <select className="yk-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {["Konut", "Ticari", "Ofis", "Villa", "Karma"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Lokasyon</label>
                  <input type="text" className="yk-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Yıl</label>
                  <input type="text" className="yk-input" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Durum</label>
                  <select className="yk-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    {["Tamamlandı", "Devam Ediyor", "Planlanan"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-8">
                  <input type="checkbox" id="featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[#C9A961]" />
                  <label htmlFor="featured" className="text-white/80 text-sm cursor-pointer">Ana sayfada öne çıkar</label>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Açıklama</label>
                <textarea rows={4} className="yk-input resize-none" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#C9A961] block mb-2">Görseller</label>
                <div className="grid grid-cols-4 gap-3 mb-3">
                  {form.images?.map((img, i) => (
                    <div key={i} className="relative aspect-square bg-[#050812] group">
                      <img src={getImg(img)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removeImage(i)} className="absolute top-1 right-1 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <label className="aspect-square border-2 border-dashed border-[#1F2937] hover:border-[#C9A961] flex flex-col items-center justify-center gap-2 cursor-pointer text-white/60 hover:text-[#C9A961] transition-colors" data-testid="upload-images-btn">
                    <Upload className="w-6 h-6" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-widest">{uploading ? "Yükleniyor" : "Yükle"}</span>
                    <input type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" disabled={uploading} />
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button onClick={save} disabled={saving} className="btn-gold flex-1 disabled:opacity-50" data-testid="save-project-btn">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
                <button onClick={close} className="btn-gold-outline flex-1">İptal</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
